/**
 * 기도 관련 API 서비스
 */

import { api } from './api';

// 기도 기록 타입
export interface PrayerRecord {
  id: string;
  userId: string;
  date: string; // YYYY-MM-DD
  duration: number; // 초 단위
  location?: string;
  content?: string;
  timestamp: string; // ISO 8601
  createdAt: string;
  updatedAt: string;
}

// 기도 통계 타입
export interface PrayerStats {
  totalDuration: number; // 총 기도 시간 (초)
  totalSessions: number; // 총 기도 세션 수
  averageDuration: number; // 평균 기도 시간 (초)
  longestSession: number; // 최장 기도 시간 (초)
  currentStreak: number; // 연속 기도 일수
  thisWeekTotal: number; // 이번 주 총 기도 시간
  thisMonthTotal: number; // 이번 달 총 기도 시간
}

// 일별 기도 통계
export interface DailyPrayerStats {
  date: string;
  totalDuration: number;
  sessionCount: number;
  records: PrayerRecord[];
}

// 기도 목표량 타입
export interface PrayerGoals {
  id: string;
  dailyPrayerGoal: number; // 일일 목표량 (분)
  weeklyPrayerGoal: number; // 주간 목표량 (분)
}

// 기도 목표 달성 상태 타입
export interface PrayerAchievement {
  id?: string;
  userId: string;
  date: string; // YYYY-MM-DD
  dailyGoal: number;
  actualDuration: number; // 실제 기도 시간 (분)
  isDailyGoalMet: boolean;
  weeklyGoal: number;
  weeklyDuration: number; // 주간 누적 기도 시간 (분)
  isWeeklyGoalMet: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// 크루 멤버 기도 달성 상태 타입
export interface CrewMemberAchievement {
  userId: string;
  name: string;
  avatar?: string;
  dailyGoal: number;
  actualDuration: number;
  isDailyGoalMet: boolean;
  hasPrayerRecord: boolean;
}

export interface CrewAchievements {
  date: string;
  members: CrewMemberAchievement[];
}

class PrayerAPI {
  // 인증 헤더 가져오기
  private getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    };
  }

  // 기도 기록 저장
  async savePrayerRecord(data: {
    duration: number;
    location?: string;
    content?: string;
  }): Promise<PrayerRecord> {
    try {
      console.log('🙏 기도 기록 저장 중...', data);
      
      const response = await api.post('/prayers', {
        duration: data.duration,
        location: data.location || '',
        content: data.content || '',
        date: new Date().toISOString().split('T')[0] // YYYY-MM-DD
      });

      console.log('✅ 기도 기록 저장 성공:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ 기도 기록 저장 실패:', error);
      
      if (error.response?.status === 401) {
        throw new Error('로그인이 필요합니다.');
      } else if (error.response?.status >= 500) {
        throw new Error('서버 오류가 발생했습니다.');
      } else {
        throw new Error(error.response?.data?.message || '기도 기록 저장에 실패했습니다.');
      }
    }
  }

  // 기도 기록 업데이트
  async updatePrayerRecord(id: string, data: {
    location?: string;
    content?: string;
  }): Promise<PrayerRecord> {
    try {
      console.log('🙏 기도 기록 업데이트 중...', { id, ...data });
      
      const response = await api.put(`/prayers/${id}`, data);

      console.log('✅ 기도 기록 업데이트 성공:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ 기도 기록 업데이트 실패:', error);
      throw new Error(error.response?.data?.message || '기도 기록 업데이트에 실패했습니다.');
    }
  }

  // 오늘의 기도 기록 조회
  async getTodayPrayerRecords(): Promise<PrayerRecord[]> {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await api.get(`/prayers/date/${today}`);
      
      return response.data || [];
    } catch (error: any) {
      console.error('❌ 오늘의 기도 기록 조회 실패:', error);
      return [];
    }
  }

  // 기도 히스토리 조회 (최근 N개)
  async getPrayerHistory(limit: number = 10): Promise<PrayerRecord[]> {
    try {
      console.log('📋 기도 히스토리 조회 중...', { limit });
      
      const response = await api.get(`/prayers/history`, {
        params: { limit }
      });

      console.log('✅ 기도 히스토리 조회 성공:', response.data);
      return response.data || [];
    } catch (error: any) {
      console.error('❌ 기도 히스토리 조회 실패:', error);
      return [];
    }
  }

  // 기도 통계 조회
  async getPrayerStats(): Promise<PrayerStats> {
    try {
      console.log('📊 기도 통계 조회 중...');
      
      const response = await api.get('/prayers/stats');

      console.log('✅ 기도 통계 조회 성공:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ 기도 통계 조회 실패:', error);
      
      // 기본값 반환
      return {
        totalDuration: 0,
        totalSessions: 0,
        averageDuration: 0,
        longestSession: 0,
        currentStreak: 0,
        thisWeekTotal: 0,
        thisMonthTotal: 0
      };
    }
  }

  // 일별 기도 통계 조회
  async getDailyPrayerStats(startDate: string, endDate: string): Promise<DailyPrayerStats[]> {
    try {
      console.log('📊 일별 기도 통계 조회 중...', { startDate, endDate });
      
      const response = await api.get('/prayers/daily-stats', {
        params: { startDate, endDate }
      });

      console.log('✅ 일별 기도 통계 조회 성공:', response.data);
      return response.data || [];
    } catch (error: any) {
      console.error('❌ 일별 기도 통계 조회 실패:', error);
      return [];
    }
  }

  // 기도 기록 삭제
  async deletePrayerRecord(id: string): Promise<void> {
    try {
      console.log('🗑️ 기도 기록 삭제 중...', { id });
      
      await api.delete(`/prayers/${id}`);

      console.log('✅ 기도 기록 삭제 성공');
    } catch (error: any) {
      console.error('❌ 기도 기록 삭제 실패:', error);
      throw new Error(error.response?.data?.message || '기도 기록 삭제에 실패했습니다.');
    }
  }

  // 기도 목표량 설정
  async setPrayerGoals(data: {
    dailyGoal: number;
    weeklyGoal?: number;
  }): Promise<PrayerGoals> {
    try {
      console.log('🎯 기도 목표량 설정 중...', data);
      
      const response = await api.put('/prayers/goals', data);

      console.log('✅ 기도 목표량 설정 성공:', response.data);
      return response.data.goals;
    } catch (error: any) {
      console.error('❌ 기도 목표량 설정 실패:', error);
      throw new Error(error.response?.data?.message || '기도 목표량 설정에 실패했습니다.');
    }
  }

  // 기도 목표량 조회
  async getPrayerGoals(): Promise<PrayerGoals> {
    try {
      console.log('🎯 기도 목표량 조회 중...');
      
      const response = await api.get('/prayers/goals');

      console.log('✅ 기도 목표량 조회 성공:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ 기도 목표량 조회 실패:', error);
      // 기본값 반환
      return {
        id: '',
        dailyPrayerGoal: 30,
        weeklyPrayerGoal: 210
      };
    }
  }

  // 기도 목표 달성 상태 조회
  async getPrayerAchievement(date?: string): Promise<PrayerAchievement> {
    try {
      console.log('🏆 기도 목표 달성 상태 조회 중...', { date });
      
      const params = date ? { date } : {};
      const response = await api.get('/prayers/achievements', { params });

      console.log('✅ 기도 목표 달성 상태 조회 성공:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ 기도 목표 달성 상태 조회 실패:', error);
      // 기본값 반환
      const today = new Date().toISOString().split('T')[0];
      return {
        userId: '',
        date: today,
        dailyGoal: 30,
        actualDuration: 0,
        isDailyGoalMet: false,
        weeklyGoal: 210,
        weeklyDuration: 0,
        isWeeklyGoalMet: false
      };
    }
  }

  // 크루 멤버들의 기도 목표 달성 상태 조회
  async getCrewAchievements(crewId: string, date?: string): Promise<CrewAchievements> {
    try {
      console.log('🏆 크루 기도 달성 상태 조회 중...', { crewId, date });
      
      const params = date ? { date } : {};
      const response = await api.get(`/prayers/crew-achievements/${crewId}`, { params });

      console.log('✅ 크루 기도 달성 상태 조회 성공:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ 크루 기도 달성 상태 조회 실패:', error);
      return {
        date: date || new Date().toISOString().split('T')[0],
        members: []
      };
    }
  }
}

// 싱글톤 인스턴스 export
export const prayerAPI = new PrayerAPI();
export default prayerAPI;
